import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, Paper, Typography } from '@mui/material';
import { ClearOutlined, CheckCircleOutline } from '@mui/icons-material';

interface SignaturePadProps {
  onSave: (signatureData: string) => void;
  onClear?: () => void;
  label?: string;
  disabled?: boolean;
  initialSignature?: string;
}

const SignaturePad: React.FC<SignaturePadProps> = ({
  onSave,
  onClear,
  label = 'Signature',
  disabled = false,
  initialSignature,
}) => {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Charger une signature existante si fournie
  React.useEffect(() => {
    if (initialSignature && sigCanvas.current) {
      sigCanvas.current.fromDataURL(initialSignature);
      setIsEmpty(false);
    }
  }, [initialSignature]);

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
      if (onClear) {
        onClear();
      }
    }
  };

  const handleSave = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL('image/png');
      onSave(signatureData);
      setIsEmpty(false);
    }
  };

  const handleBegin = () => {
    setIsEmpty(false);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        bgcolor: '#fafafa',
        border: '2px solid #e0e0e0',
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 2 }}
      >
        ✍️ {label}
      </Typography>

      <Box
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'white',
          mb: 2,
          position: 'relative',
        }}
      >
        <SignatureCanvas
          ref={sigCanvas}
          canvasProps={{
            style: {
              width: '100%',
              height: '200px',
              cursor: disabled ? 'not-allowed' : 'crosshair',
            },
          }}
          backgroundColor="white"
          penColor="black"
          onBegin={handleBegin}
          velocityFilterWeight={0.7}
          minWidth={0.5}
          maxWidth={2.5}
        />

        {isEmpty && !disabled && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              color: '#999',
              fontSize: '0.9rem',
              textAlign: 'center',
            }}
          >
            Signez ici
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<ClearOutlined />}
          onClick={handleClear}
          disabled={disabled || isEmpty}
          fullWidth
          sx={{ py: 1.5 }}
        >
          Effacer
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutline />}
          onClick={handleSave}
          disabled={disabled || isEmpty}
          fullWidth
          sx={{
            py: 1.5,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e8678 0%, #2fd66d 100%)',
            },
          }}
        >
          Valider
        </Button>
      </Box>
    </Paper>
  );
};

export default SignaturePad;
